import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronRight, ChevronLeft, Bug } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AccessibilityChecker } from './AccessibilityChecker';
import { DevPerformancePanel } from './DevPerformancePanel';
import { NetworkDebugInfo } from './NetworkDebugInfo';
import { ConnectionDebugger } from './ConnectionDebugger';
import { ServiceWorkerStatus } from './ServiceWorkerStatus';
import { FeatureFlagDebugger } from './FeatureFlagDebugger';
import { FeatureFlagController } from './FeatureFlagController';
import { AnalyticsDebugger } from './AnalyticsDebugger';

export const DevToolsPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Only show in development
  if (!import.meta.env.DEV && localStorage.getItem('showDevTools') !== 'true') {
    return null;
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  return (
    <div className="fixed right-0 top-20 z-[100] max-h-[calc(100vh-5rem)] flex">
      {/* Collapsed trigger button */}
      {!isOpen && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsOpen(true)}
          className="bg-white shadow-lg border-2 hover:shadow-xl transition-all rounded-r-none h-20 w-10 flex items-center justify-center"
          aria-label="Open dev tools"
        >
          <div className="flex flex-col items-center gap-1">
            <Bug className="h-4 w-4" />
            <ChevronLeft className="h-3 w-3" />
          </div>
        </Button>
      )}

      {/* Full panel */}
      {isOpen && (
        <div className="bg-white shadow-2xl border-l-2 w-96 max-h-full overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              <h2 className="font-semibold text-sm">Dev Tools</h2>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsOpen(false)}
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Stacked panels */}
          <div className="p-4 space-y-4">
            {/* Performance Monitor */}
            <DebugSection
              title="Performance"
              isExpanded={expandedSections.has('performance')}
              onToggle={() => toggleSection('performance')}
            >
              <div className="scale-90 origin-top-left w-[111%]">
                <DevPerformancePanel />
              </div>
            </DebugSection>

            {/* Accessibility Checker */}
            <DebugSection
              title="Accessibility"
              isExpanded={expandedSections.has('a11y')}
              onToggle={() => toggleSection('a11y')}
            >
              <AccessibilityChecker />
            </DebugSection>

            {/* Network & Connection */}
            <DebugSection
              title="Network & Connection"
              isExpanded={expandedSections.has('network')}
              onToggle={() => toggleSection('network')}
            >
              <div className="space-y-3">
                <NetworkDebugInfo />
                <ConnectionDebugger />
              </div>
            </DebugSection>

            {/* Service Worker */}
            <DebugSection
              title="Service Worker"
              isExpanded={expandedSections.has('sw')}
              onToggle={() => toggleSection('sw')}
            >
              <ServiceWorkerStatus />
            </DebugSection>

            {/* Feature Flags */}
            <DebugSection
              title="Feature Flags"
              isExpanded={expandedSections.has('flags')}
              onToggle={() => toggleSection('flags')}
            >
              <div className="space-y-3">
                <FeatureFlagDebugger />
                <FeatureFlagController />
              </div>
            </DebugSection>

            {/* Analytics */}
            <DebugSection
              title="Analytics"
              isExpanded={expandedSections.has('analytics')}
              onToggle={() => toggleSection('analytics')}
            >
              <AnalyticsDebugger />
            </DebugSection>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for collapsible sections
interface DebugSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const DebugSection = ({ title, isExpanded, onToggle, children }: DebugSectionProps) => {
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <Card className="border-2">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors p-3">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>{title}</span>
              {isExpanded ? (
                <ChevronRight className="h-4 w-4 rotate-90 transition-transform" />
              ) : (
                <ChevronRight className="h-4 w-4 transition-transform" />
              )}
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="p-3 pt-0">
            {children}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
